import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Stack,
  Divider,
} from '@mui/material'
import { FormTemplate, FormFieldType } from '@/core/models/form.model'
import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'
import MultipleCheckboxInput from '@/components/ui/inputs/MultipleCheckboxInput'
import { t } from '@lingui/macro'

interface PreviewFormDialogProps {
  open: boolean
  onClose: () => void
  template: FormTemplate
}

const PreviewFormDialogComponent: React.FC<PreviewFormDialogProps> = ({ open, onClose, template }) => {
  const renderField = (field: FormTemplate['fields'][0]) => {
    switch (field.type as FormFieldType) {
      case FormFieldType.Text:
        return <TextField fullWidth label={field.label} disabled size="small" required={field.required} />
      case FormFieldType.TextArea:
        return <TextField fullWidth label={field.label} multiline rows={3} disabled size="small" required={field.required} />
      case FormFieldType.Number:
        return <TextField fullWidth label={field.label} type="number" disabled size="small" required={field.required} />
      case FormFieldType.Boolean:
        return (
          <RadioGroupInput
            label={field.label}
            disabled={true}
            value="false"
            options={[
              { label: t`No`, value: 'false' },
              { label: t`Yes`, value: 'true' }
            ]}
            row={true}
          />
        )
      case FormFieldType.Multiple:
        return (
          <MultipleCheckboxInput
            label={field.label}
            disabled={true}
            value={[]}
            options={field.options?.map(opt => ({ value: opt, label: opt })) ?? []}
          />
        )
      case FormFieldType.Date:
        return <TextField fullWidth label={field.label} type="date" disabled size="small" required={field.required} InputLabelProps={{ shrink: true }} />
      case FormFieldType.DateTime:
        return <TextField fullWidth label={field.label} type="datetime-local" disabled size="small" required={field.required} InputLabelProps={{ shrink: true }} value="" />
      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t`Preview form template`}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {template.title}
          </Typography>
          {template.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {template.description}
            </Typography>
          )}

          <Stack spacing={3} divider={<Divider />}>
            {template.fields.map((field, index) => (
              <Box key={index}>{renderField(field)}</Box>
            ))}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t`Close`}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default PreviewFormDialogComponent
